import { ChatResponse, ChatMessage, MCPServerConfig } from '../types';
import {
  AI_SERVICE_URL,
  DICE_MCP_URL,
  GAME_MCP_URL,
  DATABASE_MCP_URL,
  BBC_SOUND_MCP_URL,
} from './workerUrls';
import { logService } from './logService';

// Default MCP servers for the game (built dynamically at runtime)
export function getDefaultMcpServers(): MCPServerConfig[] {
  return [
    { url: DICE_MCP_URL(), name: 'dice' },
    { url: GAME_MCP_URL(), name: 'game' },
    { url: DATABASE_MCP_URL(), name: 'db' },
    { url: BBC_SOUND_MCP_URL(), name: 'bbc-sound' },
  ];
}

interface ChatRequest {
  prompt: string;
  playerId?: string;  // Player ID for session-based chat history
  messages?: ChatMessage[];  // Override conversation history (backwards compatibility)
  mcpServers?: MCPServerConfig[];
  systemPrompt?: string;
  maxTurns?: number;
  useOrchestrator?: boolean;  // Use 8-phase orchestrated engine (default: true)
}

export async function sendChatMessage(
  message: string,
  options?: {
    playerId?: string;  // Player ID for session-based chat
    systemPrompt?: string;
    mcpServers?: MCPServerConfig[];
    messages?: ChatMessage[];  // Previous conversation history (backwards compatibility)
  }
): Promise<ChatResponse> {
  const mcpServers = options?.mcpServers || getDefaultMcpServers();
  const mcpNames = mcpServers.map(s => s.name).join(', ');

  // Log to console for visibility
  console.log("[UI] === SENDING CHAT MESSAGE ===");
  console.log("[UI] User message:", message.substring(0, 200) + (message.length > 200 ? "..." : ""));
  console.log("[UI] Player ID:", options?.playerId || "none");
  console.log("[UI] MCP servers:", mcpNames);
  console.log("[UI] AI Service URL:", AI_SERVICE_URL());

  const logId = logService.mcp('chat', {
    servers: mcpNames,
    promptLength: message.length,
    playerId: options?.playerId,
  }, 'pending');
  const startTime = Date.now();

  const request: ChatRequest = {
    prompt: message,
    playerId: options?.playerId,  // Use session-based history
    messages: options?.messages,  // Fallback for backwards compatibility
    systemPrompt: options?.systemPrompt,
    mcpServers,
    useOrchestrator: true,  // Use 8-phase orchestrated engine
  };

  // Store the request body for AI Console inspection
  const requestBody = JSON.stringify(request, null, 2);
  localStorage.setItem('ai_last_input', requestBody);
  window.dispatchEvent(new Event('ai-console-update'));

  try {
    const response = await fetch(`${AI_SERVICE_URL()}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: requestBody,
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorDetails = {
        status: response.status,
        error: error.error,
        details: error.details,
        errorId: error.errorId,
        mcpServersRequested: error.mcpServersRequested,
        path: error.path,
      };
      logService.updateLog(logId, {
        status: 'error',
        duration,
        message: `MCP chat failed (${response.status}): ${error.details || error.error}`,
        details: errorDetails,
      });
      throw new Error(error.details || error.error || 'Chat request failed');
    }

    const result: ChatResponse = await response.json();

    // Store the response for AI Console inspection
    localStorage.setItem('ai_last_output', JSON.stringify(result, null, 2));
    window.dispatchEvent(new Event('ai-console-update'));

    // Log response received
    console.log("[UI] === RECEIVED RESPONSE ===");
    console.log("[UI] Response length:", result.response?.length || 0, "characters");
    console.log("[UI] Tool calls:", result.toolCalls?.length || 0);
    console.log("[UI] Duration:", duration, "ms");

    // Log each tool call individually
    if (result.toolCalls && result.toolCalls.length > 0) {
      for (const toolCall of result.toolCalls) {
        const outputStr = typeof toolCall.output === 'string' ? toolCall.output.slice(0, 100) : JSON.stringify(toolCall.output).slice(0, 100);
        logService.mcp(toolCall.tool, { output: outputStr }, 'success');
      }
    }

    // Log MCP connection debug info if available
    if (result.debug) {
      const { mcpServersConnected, mcpServersFailed, mcpErrors, toolsAvailable, historyLoaded, historySaved } = result.debug;
      if (mcpServersFailed && mcpServersFailed.length > 0) {
        logService.error('MCP', `Failed to connect to: ${mcpServersFailed.join(', ')}`);
      }
      // Log detailed MCP errors if available
      if (mcpErrors && mcpErrors.length > 0) {
        for (const error of mcpErrors) {
          logService.error('MCP', error);
        }
      }
      logService.updateLog(logId, {
        status: 'success',
        duration,
        details: {
          toolsUsed: result.toolCalls?.length || 0,
          responseLength: result.response?.length || 0,
          mcpConnected: mcpServersConnected,
          mcpFailed: mcpServersFailed,
          mcpErrors: mcpErrors,
          toolsAvailable,
          historyLoaded,
          historySaved,
        },
      });
    } else {
      logService.updateLog(logId, {
        status: 'success',
        duration,
        details: {
          toolsUsed: result.toolCalls?.length || 0,
          responseLength: result.response?.length || 0,
        },
      });
    }

    return result;
  } catch (err) {
    const duration = Date.now() - startTime;
    logService.updateLog(logId, {
      status: 'error',
      duration,
      message: err instanceof Error ? err.message : 'Unknown error',
    });
    throw err;
  }
}

/**
 * Send chat message using split-phase orchestrator
 * Calls each phase individually and shows progress
 */
/**
 * Map intent action types to user-friendly descriptions
 */
function getFriendlyIntent(actionType: string): string {
  const intentMap: Record<string, string> = {
    trade_commodity: '💰 Trading commodities',
    buy_commodity: '🛒 Buying goods',
    sell_commodity: '💵 Selling goods',
    check_prices: '📊 Checking market prices',
    jump: '🚀 Jumping to another system',
    dock: '🏢 Docking at station',
    undock: '🛸 Undocking from station',
    combat_attack: '⚔️ Attacking target',
    combat_flee: '🏃 Fleeing from combat',
    conversation: '💬 Conversing',
    query_system: 'ℹ️ Getting system information',
    repair: '🔧 Repairing ship',
    refuel: '⛽ Refueling',
  };
  return intentMap[actionType] || `🎮 ${actionType.replace(/_/g, ' ')}`;
}

/**
 * Map tool calls to user-friendly descriptions
 */
function getFriendlyToolCall(toolName: string, params: any): string {
  switch (toolName) {
    case 'roll':
      return `🎲 Rolled ${params.notation || 'dice'}`;
    case 'execute_trade':
      return `💰 ${params.action === 'buy' ? 'Bought' : 'Sold'} ${params.quantity} ${params.commodity_id}`;
    case 'execute_jump':
      return `🚀 Jumped to ${params.destination_system}`;
    case 'execute_dock':
      return `🏢 Docked at ${params.station_id}`;
    case 'execute_combat':
      return `⚔️ ${params.success ? 'Won' : 'Lost'} combat`;
    default:
      return `🔧 ${toolName.replace(/_/g, ' ')}`;
  }
}

/**
 * Get phase description from pipeline document data
 */
function getPhaseDescription(phase: number, pipelineDoc: any, complete: boolean): string {
  const baseDescriptions: Record<number, string> = {
    1: 'Intent Classification',
    2: 'Game State Loading',
    3: 'RAG Query (Lore + History)',
    4: 'Narrative Extension',
    5: 'Entity Creator',
    6: 'Action Execution',
    7: 'Narrator',
    8: 'Persistence',
  };

  const base = baseDescriptions[phase] || `Phase ${phase}`;

  if (!complete) {
    return base;
  }

  // Extract details from pipeline document for completed phases
  try {
    switch (phase) {
      case 1: {
        const actionType = pipelineDoc?.control?.phase1_intent?.action_type || '';
        const friendly = getFriendlyIntent(actionType);
        return `${base} — ${friendly}`;
      }

      case 2: {
        const location = pipelineDoc?.data?.game_state?.location;
        const systemName = location?.system_name || location?.current_system || 'Unknown System';
        const messagesCount = pipelineDoc?.data?.game_state?.recent_messages?.length || 0;
        return `${base} — ${systemName} (${messagesCount} recent msgs)`;
      }

      case 3: {
        const loreCount = pipelineDoc?.data?.rag_results?.length || 0;
        return `${base} — ${loreCount} results`;
      }

      case 4: {
        const tone = pipelineDoc?.data?.narrative_plan?.tone;
        if (!tone || tone === 'unknown') {
          // Check if there's a summary we can use instead
          const summary = pipelineDoc?.data?.narrative_plan?.summary;
          return summary ? `${base} — ${summary.substring(0, 30)}...` : `${base} — Planning narrative`;
        }
        return `${base} — ${tone}`;
      }

      case 5: {
        const entityCount = pipelineDoc?.data?.entities?.filter((e: any) => e.id).length || 0;
        return `${base} — ${entityCount} entities`;
      }

      case 6: {
        const toolsCalled = pipelineDoc?.control?.phase6_action_executed?.tools_called || [];
        if (toolsCalled.length > 0) {
          const toolSummaries = toolsCalled.map((tc: any) =>
            getFriendlyToolCall(tc.tool, tc.params)
          );
          return `${base} — ${toolSummaries.join(', ')}`;
        }
        return `${base} — No actions`;
      }

      case 7: {
        const narrativeLength = pipelineDoc?.history?.player_narrative?.length || 0;
        return `${base} — ${narrativeLength} chars`;
      }

      case 8: {
        const loreStored = pipelineDoc?.control?.phase8_persisted?.lore_entries_stored || 0;
        return `${base} — ${loreStored} lore entries`;
      }

      default:
        return base;
    }
  } catch {
    return base;
  }
}

export async function sendChatMessageSplitPhase(
  message: string,
  options?: {
    playerId?: string;
    mcpServers?: MCPServerConfig[];
    onProgress?: (phase: number, description: string, complete: boolean) => void;
  }
): Promise<ChatResponse> {
  const mcpServers = options?.mcpServers || getDefaultMcpServers();
  const playerId = options?.playerId || 'player-1';

  let pipelineDoc: any = null;
  const startTime = Date.now();

  try {
    // Phase 1: Intent Classification
    options?.onProgress?.(1, getPhaseDescription(1, pipelineDoc, false), false);
    const phase1Response = await fetch(`${AI_SERVICE_URL()}/phase/1`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userMessage: message,
        playerId,
        mcpServers,
      }),
    });

    if (!phase1Response.ok) {
      throw new Error(`Phase 1 failed: ${await phase1Response.text()}`);
    }

    const phase1Result = await phase1Response.json();
    pipelineDoc = phase1Result.pipelineDoc;
    options?.onProgress?.(1, getPhaseDescription(1, pipelineDoc, true), true);

    // Phases 2-8: Sequential execution
    for (let phase = 2; phase <= 8; phase++) {
      options?.onProgress?.(phase, getPhaseDescription(phase, pipelineDoc, false), false);

      const phaseBody: Record<string, unknown> = {
        pipelineDoc,
        mcpServers,
      };

      // Phases that need additional parameters
      if (phase === 2) {
        phaseBody.playerId = playerId;
      } else if (phase === 4 || phase === 7) {
        phaseBody.userMessage = message;
      }

      const phaseResponse = await fetch(`${AI_SERVICE_URL()}/phase/${phase}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(phaseBody),
      });

      if (!phaseResponse.ok) {
        throw new Error(`Phase ${phase} failed: ${await phaseResponse.text()}`);
      }

      const phaseResult = await phaseResponse.json();
      pipelineDoc = phaseResult.pipelineDoc;
      options?.onProgress?.(phase, getPhaseDescription(phase, pipelineDoc, true), true);
    }

    const duration = Date.now() - startTime;

    // Extract final narrative from pipeline document
    const narrative = pipelineDoc.history.player_narrative;

    console.log("[UI] === SPLIT-PHASE COMPLETE ===");
    console.log("[UI] Total duration:", duration, "ms");
    console.log("[UI] Narrative length:", narrative.length, "characters");

    return {
      response: narrative,
      usage: undefined, // Token usage not tracked in split-phase mode
      debug: {
        mcpServersRequested: mcpServers.length,
        mcpServersConnected: mcpServers.map(s => s.name || 'unknown'),
        mcpServersFailed: [],
        toolsAvailable: 0,
        toolNames: [],
      },
    };
  } catch (err) {
    console.error("[UI] Split-phase error:", err);
    throw err;
  }
}

export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${AI_SERVICE_URL()}/health`);
    return response.ok;
  } catch {
    return false;
  }
}
